import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';

interface ReturnedItemRow {
  order_id: string | null;
  order_number: string | null;
  returned_at: string | null;
  item_id: string;
  item_name: string;
  brand_name: string;
  credit_price: number;
  slug: string | null;
  current_status:
    | 'draft'
    | 'coming_soon'
    | 'available'
    | 'reserved'
    | 'rented_out'
    | 'returning'
    | 'archived'
    | null;
  main_image_url: string | null;
}

/**
 * Inventory recovery view — every item that has come back via a returned
 * order. The admin clicks "Put back live" to flip the item.status to
 * available so customers can rent it again. Items that are already
 * available (a previous click happened, or the customer returned a
 * different copy) just show "Already live".
 */
export function AdminReturnedItems() {
  const [rows, setRows] = useState<ReturnedItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  function load() {
    setLoading(true);
    apiFetch<ReturnedItemRow[]>('/api/admin/returned-items')
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function makeAvailable(itemId: string) {
    setWorking(itemId);
    setError(null);
    try {
      await apiFetch(`/api/admin/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'available' })
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setWorking(null);
    }
  }

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      r.item_name.toLowerCase().includes(q) ||
      r.brand_name.toLowerCase().includes(q) ||
      (r.order_number ?? '').toLowerCase().includes(q) ||
      (r.slug ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Returned items</h1>
          <div className="sub">
            Items from orders marked &ldquo;Returned&rdquo;. Put them back live
            to make them rentable again.
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-card">
        <div className="a-toolbar">
          <div className="a-search" style={{ flex: 1, minWidth: 200 }}>
            <span className="ic">
              <SearchIcon />
            </span>
            <input
              placeholder="Search by item, brand, order #…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="a-empty">
            <div className="h">Nothing returned yet</div>
            <div className="p">
              Items show up here once you mark an order as returned in the Orders tab.
            </div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Image</th>
                  <th>Item</th>
                  <th>Brand</th>
                  <th style={{ width: 90 }}>Credits</th>
                  <th style={{ width: 110 }}>Current status</th>
                  <th>From order</th>
                  <th style={{ width: 130 }}>Returned</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isLive = r.current_status === 'available';
                  return (
                    <tr key={r.item_id}>
                      <td>
                        <div
                          className="thumb"
                          style={
                            r.main_image_url
                              ? { backgroundImage: `url(${r.main_image_url})` }
                              : undefined
                          }
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {r.slug ? (
                            <Link to={`/admin/items/${r.item_id}`}>
                              {r.item_name}
                            </Link>
                          ) : (
                            r.item_name
                          )}
                        </div>
                        {r.slug && <div className="id">{r.slug}</div>}
                      </td>
                      <td>{r.brand_name}</td>
                      <td className="num">{r.credit_price}</td>
                      <td>
                        <span
                          className={`badge ${
                            isLive
                              ? 'badge-good'
                              : r.current_status === null
                                ? 'badge-neutral'
                                : 'badge-warn'
                          }`}
                        >
                          {r.current_status?.replace(/_/g, ' ') ?? 'deleted'}
                        </span>
                      </td>
                      <td>
                        {r.order_id && r.order_number ? (
                          <Link to={`/admin/orders/${r.order_id}`} className="id">
                            {r.order_number}
                          </Link>
                        ) : (
                          <span style={{ color: 'var(--a-ink-soft)' }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.returned_at
                          ? new Date(r.returned_at).toLocaleDateString('en-GB')
                          : '—'}
                      </td>
                      <td>
                        {r.current_status === null ? (
                          <span
                            style={{
                              fontSize: 11,
                              color: 'var(--a-ink-soft)'
                            }}
                          >
                            Item deleted
                          </span>
                        ) : isLive ? (
                          <span
                            style={{
                              fontSize: 11,
                              color: 'var(--a-good, #1f7a3d)',
                              fontWeight: 600
                            }}
                          >
                            ★ Already live
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={working === r.item_id}
                            onClick={() => void makeAvailable(r.item_id)}
                          >
                            {working === r.item_id
                              ? 'Updating…'
                              : 'Put back live'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
